import React from 'react';

import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { filter, includes, toLower } from 'es-toolkit/compat';

import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from 'src/providers/PaginationProvider';

import { PeoPayrollProviderLiveStatusValues } from '../types';

import { PeoPayrollProviderLiveStatusRow } from './PeoPayrollProviderLiveStatusRow';
import { rowStyles, searchStyles } from './style';

export type PeoPayrollProviderLiveStatusFormProps = {
  values: PeoPayrollProviderLiveStatusValues['employers'];
  setPeoLiveStatus: (peoId: string) => void;
  setEmployerLiveStatus: (employerId: string) => void;
  setEmployerUnderPeoLiveStatus: (peoId: string, employerId: string) => void;
  loading: boolean;
};

export const PeoPayrollProviderLiveStatusTable: React.FC<PeoPayrollProviderLiveStatusFormProps> = ({
  values,
  loading,
  setEmployerLiveStatus,
  setPeoLiveStatus,
  setEmployerUnderPeoLiveStatus,
}) => {
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = React.useMemo(() => {
    const searchedValues = search
      ? filter(values, value => {
          if (includes(toLower(value.displayName), toLower(search))) return true;
          if (
            value.employers?.some(employer =>
              includes(toLower(employer.displayName), toLower(search)),
            )
          )
            return true;
          return false;
        })
      : values;

    return searchedValues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, search, values]);

  if (loading) {
    return <CircularProgress />;
  }
  return (
    <React.Fragment>
      <TextField
        label="Employer Display Name"
        variant="outlined"
        sx={searchStyles}
        value={search}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSearch(event.target.value);
        }}
      />
      <TableContainer>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={rowStyles}>
              <TableCell>Name</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map(value => (
              <PeoPayrollProviderLiveStatusRow
                key={value.id}
                row={value}
                setEmployerLiveStatus={setEmployerLiveStatus}
                setPeoLiveStatus={setPeoLiveStatus}
                setEmployerUnderPeoLiveStatus={setEmployerUnderPeoLiveStatus}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={values.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
};
